import { debug } from '@/config/constants';

/**
 * Device Lock Service
 * Prevents multiple concurrent calls/queue entries from the same browser/device
 * Uses localStorage to track active sessions across tabs
 */

const ACTIVE_SESSION_KEY = 'activeSessionId';

class DeviceLockService {
  /**
   * Check if device has an active session
   */
  hasActiveSession(): boolean {
    const activeSessionId = localStorage.getItem(ACTIVE_SESSION_KEY);
    return activeSessionId !== null;
  }

  /**
   * Get the active session ID (if any)
   */
  getActiveSessionId(): string | null {
    return localStorage.getItem(ACTIVE_SESSION_KEY);
  }

  /**
   * Acquire lock for a new session
   * Returns true if lock acquired or same session re-acquiring
   * Returns false if another session is active
   */
  acquireLock(sessionId: string): boolean {
    const existingSession = localStorage.getItem(ACTIVE_SESSION_KEY);
    
    if (existingSession && existingSession !== sessionId) {
      debug.log(`Device lock: BLOCKED - held by ${existingSession.substring(0, 30)}, requested by ${sessionId.substring(0, 30)}`);
      return false;
    }
    
    localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
    debug.log(`Device lock: ACQUIRED for ${sessionId.substring(0, 30)}`);
    return true;
  }

  /**
   * Release lock for a session
   */
  releaseLock(sessionId: string): void {
    const activeSession = localStorage.getItem(ACTIVE_SESSION_KEY);
    
    if (activeSession === sessionId) {
      localStorage.removeItem(ACTIVE_SESSION_KEY);
      debug.log(`Device lock: RELEASED for ${sessionId.substring(0, 30)}`);
    } else {
      debug.log(`Device lock: SKIP release - held by ${activeSession?.substring(0, 30) || 'none'}, not ${sessionId.substring(0, 30)}`);
    }
  }
}

export const deviceLockService = new DeviceLockService();
